import { onlineManager } from '@tanstack/react-query';
import * as R from 'ramda';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../../../../../components/Hooks/useWidth';
import { useModalState } from '../../../../../../../../hooks/useModalState';
import storage from '../../../../../../../../hooks/useStorage/useStorage';
import useOrderBookStore from '../../../../../../../../stores/OrderBook/orderBook.store';
import { OrderBookOrder } from '../../../../../../../../stores/OrderBook/orderBook.types';
import useProductDetailsStore from '../../../../../../../../stores/ProductDetails/productDetails.store';
import useOrderBook from '../../../../../../../order-book/hooks/useOrderBook';
import { useProductGrid } from '../../../../../../../orders/products/product/grid/components/orderProductGrid/hooks/useProductGrid/useProductGrid';
import { AvailabilityEnum } from '../../../../../../ProductTypes';
import { availabilityToCompactAvailability } from '../../../../../../utils/availability.utils';
import { useProductLock } from '../../../../../hooks/useProductLock';
import { Combination } from '../../../../CombinationLine/types/combinationLine.types';
import { UseKitItemCombinationProps } from '../types/kitItemCombination.types';

const useKitItemCombination = ({ combination }: UseKitItemCombinationProps) => {
  const { mobileWidth } = useWidth();
  const { allOrders } = useOrderBook();
  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);
  const { isOpen: isLoginModalOpen, closeModal: closeLoginModal } = useModalState();
  const { getLimit, getStockValue } = useProductLock();
  const {
    isFinishedOrder,
    copiedGrid,
    productGridParams,
    handleChangeProductGridParams,
    setProductDetailsModalState,
  } = useProductDetailsStore(
    useShallow((state) => ({
      isFinishedOrder: state.productGridParams.isFinishedOrder,
      copiedGrid: state.copiedGrid,
      productGridParams: state.productGridParams,
      handleChangeProductGridParams: state.handleChangeProductGridParams,
      setProductDetailsModalState: state.setProductDetailsModalState,
    })),
  );

  const {
    grid,
    showSuggestedGrid,
    suggestedGrid,
    hasValidPacks,
    selectedPacks,
    suggestedPacks,
    suggestionAchieved,
  } = useProductGrid({
    availability: combination.availability,
    productColorId: combination.productColorId,
    deliveryWindowId: combination.deliveryWindowId,
    kitId: combination.kitId,
    isOfflineMode: !onlineManager.isOnline(),
    useDefaultPriceTableWhenOffline: true,
  });

  const combinationFormatted: Combination = {
    productColorIsImage: !!combination.productColorImageUrl,
    productColorId: combination.productColorId,
    productColorName: combination.productColorName,
    productColorCode: combination.productColorCode,
    productColorHexCode: combination.productColorHexCode ?? undefined,
    productColorImageUrl: combination.productColorImageUrl ?? undefined,
    productId: combination.productId,
    productCode: combination.productCode,
    productName: combination.productName,
    allowChangePreOrderGrid: combination.allowChangePreOrderGrid,
    allowChangeImmediateGrid: combination.allowChangeImmediateGrid,
    futureStockId: null,
    futureStockDeliveryWindowId: null,
    deliveryWindowId: combination.deliveryWindowId,
    deliveryWindowName: combination.deliveryWindowName,
    deliveryWindowStartDate: combination.deliveryWindowStartDate ?? null,
    deliveryWindowEndDate: combination.deliveryWindowEndDate ?? null,
    availability: combination.availability,
  };

  const formattedGrid = grid.map((gridItem) => {
    const skuKit = gridItem.skuKits?.find((skuKit) => skuKit.kitId === combination.kitId);
    const totalAmount = gridItem.stock?.availableAmount;
    const itemQuantity = skuKit ? skuKit.quantity : undefined;
    const doesNotHaveStock = combination.availability === AvailabilityEnum.INFINITE_PRE_ORDER;
    const isInvalid =
      !doesNotHaveStock &&
      !R.isNil(totalAmount) &&
      !R.isNil(itemQuantity) &&
      itemQuantity > totalAmount;

    return {
      ...gridItem,
      available: gridItem.available && !isInvalid,
      orderItemQuantity: skuKit ? skuKit.quantity : undefined,
    };
  });

  const handleOpenProductDetails = () => {
    const productDetailsFilterSelectedByUser = storage.getProductDetailsFilter(
      storage.getSessionId() as string,
    );
    const isAllOrdersFilterSelectedByUser = productDetailsFilterSelectedByUser?.applyAllOrders;
    const orderIds = (() => {
      if (isAllOrdersFilterSelectedByUser && !mobileWidth) {
        return allOrders.map((order: OrderBookOrder) => order.id);
      }

      if (selectedOrder) {
        return [selectedOrder.id];
      }

      return [];
    })();

    handleChangeProductGridParams({
      ...productGridParams,
      productColorId: combination.productColorId,
      availability: availabilityToCompactAvailability(combination.availability),
      isFinishedOrder: false,
      orderIds,
    });
    setProductDetailsModalState({
      isOpen: true,
      productColorId: combination.productColorId,
      productId: combination.productId,
    });
  };

  return {
    isLoginModalOpen,
    closeLoginModal,
    grid,
    formattedGrid,
    combinationFormatted,
    showSuggestedGrid,
    suggestedGrid,
    hasValidPacks,
    selectedPacks,
    suggestedPacks,
    suggestionAchieved,
    isFinishedOrder,
    copiedGrid,
    getLimit,
    getStockValue,
    handleOpenProductDetails,
  };
};

export default useKitItemCombination;
